// MemberRoleControl — change one household member's role in place.
//
// Donna, 2026-07-31: the Cleanup research drawer told her, on a
// household with two members both marked "primary", to "open the extra
// member below and set its role to spouse / child / other." She opened
// the member and there was no such control — anywhere in the OS. A role
// could only be set while ADDING a contact or MOVING one to a DIFFERENT
// household.
//
// One component, mounted in the two places people actually look: the
// household's Members tab, and the Cleanup research drawer's member rows
// (so a fix never means leaving the queue — the same reasoning that put
// CleanupInlineEdit there). Both call the same endpoint, so the two
// screens cannot drift apart in behavior or in wording.
//
// Deliberately a native <select>: it is the same primitive the "+ Member"
// form's role picker already uses, it is keyboard- and screen-reader-
// correct for free, and the Cleanup queue is a keyboard-first surface.
//
// Styled inline rather than via styles.css so the component is fully
// self-contained across its two hosts — one of which (the Members tab)
// is className-styled and the other (the Cleanup drawer) is inline-
// styled throughout. Tokens only; no hard-coded colors.

import { useState } from "react";

import { api, ApiError } from "../api";
import type { HouseholdRole } from "@shared/types/enums";
import { humanizeHouseholdRole } from "@shared/lib/household-roles";
import {
  memberRoleChangeMessage,
  memberRoleOptions,
  memberRoleRefusalMessage,
} from "./member-role-copy";

export interface MemberRoleControlProps {
  householdId: string;
  contactId: string;
  memberName: string;
  role: string | null;
  /** True when this contact IS households.primary_contact_id. */
  isDesignatedPrimary: boolean;
  /**
   * Names keyed by contact id, so a promotion can say WHO got demoted
   * rather than "another member". Missing ids fall back to that phrase.
   */
  memberNamesById?: Record<string, string>;
  /** Fired after a change that actually wrote something. */
  onChanged: (message: string) => void;
  onError: (message: string) => void;
  disabled?: boolean;
  /** Compact styling for the 480px Cleanup drawer. */
  compact?: boolean;
}

export function MemberRoleControl({
  householdId,
  contactId,
  memberName,
  role,
  isDesignatedPrimary,
  memberNamesById,
  onChanged,
  onError,
  disabled = false,
  compact = false,
}: MemberRoleControlProps) {
  const [saving, setSaving] = useState(false);

  // The designated primary's role is not editable here — demoting them
  // would leave the household with no primary at all, which is the very
  // condition the cleanup queue flags. Show the reason up front rather
  // than letting someone pick a value that can only be refused.
  if (isDesignatedPrimary) {
    return (
      <span
        style={{
          fontSize: compact ? 11 : 12,
          fontWeight: 700,
          color: "var(--fg-3)",
          whiteSpace: "nowrap",
          cursor: "help",
        }}
        title={memberRoleRefusalMessage(memberName)}
      >
        Primary contact
      </span>
    );
  }

  const options = memberRoleOptions(false);
  // An out-of-vocabulary stored value (pre-enum data) is preserved as its
  // own option rather than silently rewritten to whatever the dropdown
  // happens to show first — the same guard the contact edit form uses on
  // its marital-status field.
  const current = (role ?? "").trim().toLowerCase();
  const unknownCurrent =
    current !== "" && !options.some((o) => o.value === current);

  async function change(next: string) {
    if (next === current || saving) return;
    setSaving(true);
    try {
      const result = await api.setHouseholdMemberRole(
        householdId,
        contactId,
        next as HouseholdRole,
      );
      if (!result.changed) {
        // Nothing was written. Say so rather than claiming a save.
        onChanged(`${memberName} was already ${humanizeHouseholdRole(next).toLowerCase()}.`);
        return;
      }
      const demotedNames = result.demoted_contact_ids.map(
        (id) => memberNamesById?.[id] ?? "another member",
      );
      onChanged(
        memberRoleChangeMessage(memberName, next as HouseholdRole, demotedNames),
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        onError(err.message);
      } else {
        onError(
          err instanceof Error ? err.message : "Couldn't change the role.",
        );
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <label
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        fontSize: compact ? 11 : 12,
        color: "var(--fg-3)",
        whiteSpace: "nowrap",
      }}
    >
      <span
        style={{
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          fontSize: compact ? 9.5 : 10.5,
          color: "var(--fg-muted)",
        }}
      >
        Role
      </span>
      <select
        value={unknownCurrent ? current : current || "member"}
        disabled={disabled || saving}
        aria-label={`Role of ${memberName} in this household`}
        onChange={(e) => void change(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        style={{
          border: "1px solid var(--mid-gray)",
          borderRadius: 6,
          background: "var(--bg-0)",
          color: "var(--navy)",
          fontSize: compact ? 11.5 : 12.5,
          fontWeight: 600,
          padding: compact ? "2px 4px" : "3px 6px",
          cursor: disabled || saving ? "default" : "pointer",
        }}
      >
        {unknownCurrent && (
          <option value={current}>
            {humanizeHouseholdRole(current)} (existing)
          </option>
        )}
        {options.map((o) => (
          <option key={o.value} value={o.value} title={o.hint}>
            {o.label}
          </option>
        ))}
      </select>
      {saving && (
        <span style={{ color: "var(--fg-muted)", fontSize: compact ? 10.5 : 11.5 }}>
          Saving…
        </span>
      )}
    </label>
  );
}
